import { Machine, assign, Interpreter } from 'xstate'
import { SortOrderOption, DEFAULT_SORT_OPTION } from 'modules/brands/discovery/utils/sortOrder'

export type DisplayMode = 'grid' | 'list'

export interface MachineSchema {
  states: {
    ready: Record<string, unknown>
  }
}

export interface MachineContext {
  category: string
  filters: string[]
  demographics: string[]
  shippingDestinations: string[]
  order: SortOrderOption
  display: DisplayMode
  page: number
  perPage: number
  searchQuery: string
}

type ToggleItemAction = {
  type: 'TOGGLE_ITEM'
  key: 'filters' | 'demographics'
  item: string
}

type SetFiltersAction = {
  type: 'SET_FILTERS'
  filters: string[]
}

type ResetAction = {
  type: 'RESET'
}

type ClearAction = {
  type: 'CLEAR'
}

type SetPageAction = {
  type: 'SET_PAGE'
  page: number
}

type SetOrderAction = {
  type: 'SET_ORDER'
  order: SortOrderOption
}

type SetDisplayAction = {
  type: 'SET_DISPLAY'
  display: DisplayMode
}

type ToggleCategoryAction = {
  type: 'TOGGLE_CATEGORY'
  category: string | null
}

type SetCategoryAction = {
  type: 'SET_CATEGORY'
  category: string
}

type SetShippingDestinationAction = {
  type: 'SET_SHIPPING_DESTINATIONS'
  shippingDestinations: string[]
}

type SetSearchQueryAction = {
  type: 'SET_SEARCH_QUERY'
  searchQuery: string
}

type SearchAction = {
  type: 'SEARCH'
  searchQuery: string
}

export type MachineAction =
  | ToggleItemAction
  | ResetAction
  | ClearAction
  | SetPageAction
  | SetOrderAction
  | SetDisplayAction
  | SetShippingDestinationAction
  | ToggleCategoryAction
  | SetCategoryAction
  | SetFiltersAction
  | SetSearchQueryAction
  | SearchAction

export const defaultContext: MachineContext = Object.freeze({
  category: null,
  filters: [],
  demographics: [],
  shippingDestinations: [],
  order: DEFAULT_SORT_OPTION,
  display: 'list',
  page: 1,
  perPage: 20,
  searchQuery: ''
})

type MachineState = {
  value: 'ready'
  context: MachineContext
}

export type BrandDiscoveryService = Interpreter<MachineContext, any, MachineAction, MachineState>

export const brandDiscoveryMachine = Machine<MachineContext, MachineSchema, MachineAction>(
  {
    id: 'brandDiscovery',
    initial: 'ready',
    context: defaultContext,
    states: {
      ready: {
        on: {
          CLEAR: { actions: ['reset', 'updateRouter', 'refetchBrands'] },
          TOGGLE_ITEM: { actions: ['toggleItem', 'updateRouter', 'refetchBrands', 'logQuery'] },
          SET_FILTERS: { actions: ['setFilters', 'updateRouter', 'refetchBrands', 'logQuery'] },
          TOGGLE_CATEGORY: { actions: ['setCategory', 'updateRouter', 'refetchBrands', 'logQuery'] },
          SET_CATEGORY: { actions: ['setCategory'] },
          RESET: { actions: ['reset', 'refetchBrands'] },
          SET_PAGE: { actions: ['setPage', 'updateRouter', 'refetchBrands'] },
          SET_ORDER: { actions: ['setOrder', 'updateRouter', 'refetchBrands'] },
          SET_SHIPPING_DESTINATIONS: { actions: ['setShippingDestinations', 'updateRouter', 'refetchBrands', 'logQuery'] },
          SET_DISPLAY: { actions: ['setDisplay', 'updateRouter'] },
          SET_SEARCH_QUERY: { actions: ['setSearchQuery'] },
          SEARCH: { actions: ['setSearchQuery', 'updateRouter', 'refetchBrands', 'logQuery'] }
        }
      }
    }
  },
  {
    actions: {
      toggleItem: assign<MachineContext, ToggleItemAction>((ctx, { key, item }) => {
        const items = ctx[key]

        if (items.includes(item)) {
          return {
            [key]: items.filter((curr) => curr !== item),
            page: 1
          }
        } else {
          return {
            [key]: [...items, item],
            page: 1
          }
        }
      }) as any,
      setFilters: assign<MachineContext, SetFiltersAction>((ctx, { filters }) => ({
        filters,
        page: 1
      })) as any,
      setCategory: assign<MachineContext, ToggleCategoryAction>((ctx, { category }) => {
        if (category === null) {
          return {
            category: null,
            page: 1
          }
        } else {
          return {
            category,
            page: 1
          }
        }
      }) as any,
      reset: assign<MachineContext, ResetAction>(() => defaultContext) as any,
      setPage: assign<MachineContext, SetPageAction>((_, { page }) => ({
        page
      })) as any,
      setOrder: assign<MachineContext, SetOrderAction>((_, { order }) => ({
        order
      })) as any,
      setDisplay: assign<MachineContext, SetDisplayAction>((_, { display }) => ({
        display
      })) as any,
      setShippingDestinations: assign<MachineContext, SetShippingDestinationAction>((_, { shippingDestinations }) => ({
        shippingDestinations
      })) as any,
      setSearchQuery: assign<MachineContext, SearchAction>((_, { searchQuery }) => ({
        searchQuery
      })) as any,

      updateRouter: () => {},
      refetchBrands: () => {},
      logQuery: () => {}
    }
  }
)
